import type { CaptureResult } from 'posthog-js'

/**
 * Messages remontés à PostHog qui ne sont pas des exceptions applicatives.
 *
 * `ResizeObserver loop completed with undelivered notifications.` (et sa variante
 * `ResizeObserver loop limit exceeded`) est une *notification* du navigateur, pas
 * une erreur : le navigateur signale qu'il n'a pas pu livrer tous les callbacks
 * dans la même frame, et il a déjà reporté le reste à la frame suivante.
 *
 * Elle arrive via window.onerror, donc sans objet Error : PostHog la capture en
 * `synthetic: true` avec pour seule « frame » l'URL de la page. Il n'y a aucune
 * stack à exploiter, et le regroupement se fait par URL — chaque page visitée
 * crée donc une issue distincte, qui noie les vraies erreurs.
 */
const IGNORED_EXCEPTION_PATTERNS: RegExp[] = [
  // Non ancré : PostHog encapsule parfois le message
  // (`'Error' captured as exception with message: '...'`).
  /ResizeObserver loop/,

  // --- Bruit tiers : scripts d'extensions navigateur injectés dans la page ---

  // API chrome.runtime : inatteignable depuis le code applicatif, donc
  // forcément une extension.
  /Invalid call to runtime\.sendMessage\(\)/,

  // Message interne d'extension. Volontairement spécifique plutôt que de filtrer
  // tous les « Unhandled error response received for message <…> » : mieux vaut
  // laisser passer un inconnu que masquer une vraie erreur.
  /Unhandled error response received for message <get-frame-manager-configuration>/,

  // Erreur cross-origin dont le navigateur masque tout le détail (message, ligne,
  // stack) : rien d'exploitable. Nos assets sont servis depuis la même origine que
  // l'app, donc ces événements viennent de scripts tiers. Encadré par `'` ou par
  // les bornes de la chaîne pour ne pas attraper un vrai message la contenant.
  /(^|')Script error\.?('|$)/,

  // posthog-js : le script lazy-loaded de session replay bloqué (ad blocker, CSP…).
  // Bruit interne SDK, pas une erreur applicative. posthog-js >= 1.429 logue un warn
  // au lieu de throw ; le filtre couvre les bundles encore en cache.
  /Called on script loaded before session recording is available/,
]

interface ExceptionListEntry {
  value?: unknown
}

/** Messages portés par un event $exception, quel que soit le format PostHog. */
function readExceptionMessages(event: CaptureResult): string[] {
  const { $exception_list: list, $exception_values: values } = event.properties ?? {}

  const messages: string[] = []

  if (Array.isArray(list)) {
    for (const entry of list as ExceptionListEntry[]) {
      if (typeof entry?.value === 'string') messages.push(entry.value)
    }
  }

  if (Array.isArray(values)) {
    for (const value of values) {
      if (typeof value === 'string') messages.push(value)
    }
  }

  return messages
}

export function isIgnoredExceptionEvent(event: CaptureResult | null): boolean {
  if (!event || event.event !== '$exception') return false

  return readExceptionMessages(event).some((message) =>
    IGNORED_EXCEPTION_PATTERNS.some((pattern) => pattern.test(message))
  )
}

/**
 * Hook `before_send` : renvoyer null abandonne l'event. On ne touche qu'aux
 * `$exception` listés ci-dessus, tout le reste passe inchangé.
 */
export function filterIgnoredExceptions(event: CaptureResult | null): CaptureResult | null {
  return isIgnoredExceptionEvent(event) ? null : event
}
